/**
 * MA-ARCH-181: Attribution Confidence Badge
 * 
 * Shared badge component mapping backend confidence levels to user-facing labels.
 * Consistent across Lead Detail, Lead List, LP Performance, and PP Detail.
 * 
 * deterministic → High Confidence (emerald)
 * inferred     → Medium Confidence (amber)
 * unknown      → Unknown (slate)
 */

import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";
import { cn } from "@/lib/utils";

const CONFIDENCE_CONFIG = {
  deterministic: {
    label: "High Confidence",
    className: "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25",
    icon: ShieldCheck,
  },
  inferred: {
    label: "Medium Confidence",
    className: "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/25",
    icon: ShieldAlert,
  },
  unknown: {
    label: "Unknown",
    className: "bg-slate-500/15 text-slate-400 ring-1 ring-slate-500/25",
    icon: ShieldQuestion,
  },
};

export function getConfidenceConfig(confidence) {
  return CONFIDENCE_CONFIG[confidence] || CONFIDENCE_CONFIG.unknown;
}

export function ConfidenceBadge({ confidence, compact = false, className }) {
  const config = getConfidenceConfig(confidence);
  const Icon = config.icon;

  return (
    <Badge
      className={cn(
        "font-normal gap-1",
        compact ? "h-5 text-[10px] px-1.5" : "h-6 text-xs px-2",
        config.className,
        className,
      )}
      data-testid={`confidence-badge-${confidence || "unknown"}`}
    >
      <Icon className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} />
      {config.label}
    </Badge>
  );
}

/**
 * Inline source label with consistent styling.
 */
export function SourceLabel({ source }) {
  if (!source) return <span className="text-slate-500 text-sm">Not available</span>;

  const display = source
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <span className="text-sm text-slate-200" data-testid="attribution-source-label">
      {display}
    </span>
  );
}
